import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Drawer,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  AssignmentOutlined as EmptyIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import api from '../services/api';
import ModernToast from '../components/molecules/ModernToast/ModernToast';
import BottomSheetHandle from '../components/atoms/BottomSheetHandle';
import HeaderPrimaryAction from '../components/atoms/HeaderPrimaryAction';

const SECTIONS = {
  themes: {
    label: 'Temas Trabalhados',
    titleField: 'theme',
    fields: [
      { key: 'date', label: 'Data', type: 'date', required: true },
      { key: 'theme', label: 'Tema', required: true },
      { key: 'description', label: 'Descricao/Contexto', multiline: true },
      { key: 'objective', label: 'Objetivo' },
      { key: 'notes', label: 'Observacoes', multiline: true },
    ],
    columns: ['date', 'theme', 'objective', 'notes'],
  },
  contents: {
    label: 'Conteudos Divulgados',
    titleField: 'title',
    fields: [
      { key: 'date', label: 'Data', type: 'date', required: true },
      { key: 'type', label: 'Tipo', select: ['Release', 'Aviso de pauta', 'Artigo', 'Questionario', 'Nota oficial', 'Sugestao de pauta', 'Entrevista', 'Outro'] },
      { key: 'title', label: 'Titulo', required: true },
      { key: 'spokespersons', label: 'Porta-vozes' },
      { key: 'relatedTheme', label: 'Tema relacionado' },
      { key: 'notes', label: 'Observacoes', multiline: true },
    ],
    columns: ['date', 'type', 'title', 'spokespersons'],
  },
  actions: {
    label: 'Atendimentos / Acoes',
    titleField: 'agenda',
    fields: [
      { key: 'date', label: 'Data', type: 'date', required: true },
      { key: 'vehicle', label: 'Veiculo' },
      { key: 'agenda', label: 'Pauta', required: true },
      { key: 'spokespersons', label: 'Porta-voz' },
      { key: 'account', label: 'Conta' },
      { key: 'action', label: 'Acao', select: ['Atendimento a imprensa', 'Follow-up', 'Entrevista articulada', 'Sugestao de pauta', 'Resposta enviada', 'Agendamento', 'Reuniao', 'Recusa registrada', 'Outro'] },
      { key: 'status', label: 'Status/OBS' },
      { key: 'refusalReason', label: 'Motivo da recusa' },
      { key: 'notes', label: 'Observacoes', multiline: true },
    ],
    columns: ['date', 'vehicle', 'agenda', 'spokespersons', 'action', 'status'],
  },
};

const emptyForm = (section) => ({
  section,
  date: new Date().toISOString().slice(0, 10),
  theme: '',
  description: '',
  objective: '',
  type: '',
  title: '',
  spokespersons: '',
  relatedTheme: '',
  vehicle: '',
  agenda: '',
  account: '',
  action: '',
  status: '',
  refusalReason: '',
  notes: '',
});

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('pt-BR');
};

const toInputDate = (value) => {
  if (!value) return new Date().toISOString().slice(0, 10);
  return new Date(value).toISOString().slice(0, 10);
};

const PRControlsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [section, setSection] = React.useState('themes');
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm('themes'));
  const [toast, setToast] = React.useState({ open: false, message: '', severity: 'success' });
  const [sectionDrawerOpen, setSectionDrawerOpen] = React.useState(false);

  const sectionConfig = SECTIONS[section];

  const loadItems = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/pr-controls', { params: { section } });
      setItems(response.data.data || []);
    } catch (error) {
      setToast({ open: true, message: 'Erro ao carregar controles de PR', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [section]);

  React.useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyForm(section));
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({ ...emptyForm(section), ...item, date: toInputDate(item.date) });
    setDialogOpen(true);
  };

  const saveItem = async () => {
    const requiredField = sectionConfig.fields.find(field => field.required && !String(form[field.key] || '').trim());
    if (requiredField) {
      setToast({ open: true, message: `${requiredField.label} e obrigatorio`, severity: 'warning' });
      return;
    }

    try {
      if (editingItem) {
        await api.put(`/pr-controls/${editingItem._id}`, form);
      } else {
        await api.post('/pr-controls', form);
      }
      setDialogOpen(false);
      setToast({ open: true, message: 'Controle de PR salvo', severity: 'success' });
      loadItems();
    } catch (error) {
      setToast({ open: true, message: 'Erro ao salvar controle de PR', severity: 'error' });
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm('Deseja remover este registro?')) return;
    try {
      await api.delete(`/pr-controls/${item._id}`);
      setToast({ open: true, message: 'Registro removido', severity: 'success' });
      loadItems();
    } catch (error) {
      setToast({ open: true, message: 'Erro ao remover registro', severity: 'error' });
    }
  };

  const renderValue = (item, key) => {
    if (key === 'date') return formatDate(item[key]);
    return item[key] || '-';
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'center', mb: { xs: 2, md: 3 } }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.65rem', md: '2.125rem' }, lineHeight: 1.1 }}>Controles de PR</Typography>
          <Typography variant="body2" color="text.secondary">
            Inputs operacionais do assessor para alimentar os resultados de PR.
          </Typography>
        </Box>
        <HeaderPrimaryAction
          label="Novo registro"
          ariaLabel="Criar novo registro de PR"
          onClick={openCreate}
        />
      </Box>

      {isMobile && (
        <Button fullWidth variant="outlined" onClick={(event) => { event.currentTarget.blur(); setSectionDrawerOpen(true); }} endIcon={<ExpandMoreIcon />} sx={{ mb: 2, minHeight: 46, justifyContent: 'space-between', textTransform: 'none', fontWeight: 800 }}>
          {sectionConfig.label}
        </Button>
      )}

      <Drawer anchor="bottom" open={isMobile && sectionDrawerOpen} onClose={() => setSectionDrawerOpen(false)} ModalProps={{ disableRestoreFocus: true }} PaperProps={{ sx: { borderRadius: '18px 18px 0 0', px: 2, pt: 1, pb: 'calc(20px + env(safe-area-inset-bottom, 0px))' } }}>
        <BottomSheetHandle sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box><Typography sx={{ fontWeight: 850 }}>Tipo de controle</Typography><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Escolha os registros que deseja visualizar</Typography></Box>
          <IconButton onClick={() => setSectionDrawerOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <List disablePadding>
          {Object.entries(SECTIONS).map(([key, config]) => (
            <ListItemButton key={key} selected={section === key} onClick={(event) => { event.currentTarget.blur(); setSection(key); setSectionDrawerOpen(false); }} sx={{ minHeight: 52, borderRadius: 2, mb: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 38 }}><EmptyIcon color={section === key ? 'primary' : 'disabled'} /></ListItemIcon>
              <ListItemText primary={config.label} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: section === key ? 800 : 650 }} />
              {section === key && <CheckIcon color="primary" />}
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Paper sx={{ display: { xs: 'none', md: 'block' }, mb: 3, overflow: 'hidden' }}>
        <Tabs value={section} onChange={(event, value) => setSection(value)} variant="scrollable" scrollButtons="auto">
          {Object.entries(SECTIONS).map(([key, config]) => (
            <Tab key={key} value={key} label={config.label} />
          ))}
        </Tabs>
      </Paper>

      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {sectionConfig.columns.map(column => (
                <TableCell key={column}>{sectionConfig.fields.find(field => field.key === column)?.label || column}</TableCell>
              ))}
              <TableCell align="right">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => (
              <TableRow key={item._id} hover>
                {sectionConfig.columns.map(column => (
                  <TableCell key={column}>{renderValue(item, column)}</TableCell>
                ))}
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(item)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => deleteItem(item)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && (
              <TableRow>
                <TableCell colSpan={sectionConfig.columns.length + 1} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {loading ? 'Carregando...' : 'Nenhum registro cadastrado.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.25 }}>
        {items.map(item => (
          <Card key={item._id} variant="outlined" sx={{ borderRadius: 2.5 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.3 }}>{item[sectionConfig.titleField] || 'Sem título'}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>{formatDate(item.date)}</Typography>
                </Box>
                <Box sx={{ display: 'flex' }}>
                  <IconButton size="small" aria-label="Editar registro" onClick={() => openEdit(item)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" aria-label="Excluir registro" onClick={() => deleteItem(item)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 1 }}>
                {sectionConfig.columns.filter(column => column !== 'date' && column !== sectionConfig.titleField && item[column]).map(column => (
                  <Chip key={column} size="small" label={`${sectionConfig.fields.find(field => field.key === column)?.label || column}: ${renderValue(item, column)}`} sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                ))}
              </Box>
            </CardContent>
          </Card>
        ))}
        {!items.length && (
          <Paper variant="outlined" sx={{ py: 5, px: 2, textAlign: 'center', borderRadius: 2.5 }}>
            <EmptyIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography sx={{ fontWeight: 800 }}>{loading ? 'Carregando...' : `Nenhum registro em ${sectionConfig.label.toLowerCase()}`}</Typography>
            {!loading && <><Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5, mb: 2 }}>Cadastre o primeiro item para alimentar os resultados de PR.</Typography><Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Criar primeiro registro</Button></>}
          </Paper>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle>{editingItem ? 'Editar registro' : 'Novo registro'} · {sectionConfig.label}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {sectionConfig.fields.map(field => (
              <Grid item xs={12} sm={field.multiline ? 12 : 6} key={field.key}>
                <TextField
                  fullWidth
                  label={field.label}
                  type={field.type || 'text'}
                  value={form[field.key] || ''}
                  required={field.required}
                  multiline={field.multiline}
                  rows={field.multiline ? 3 : 1}
                  select={Array.isArray(field.select)}
                  InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                  onChange={(event) => setForm(prev => ({ ...prev, [field.key]: event.target.value }))}
                >
                  {Array.isArray(field.select) && field.select.map(option => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveItem}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <ModernToast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default PRControlsPage;
